import { useState, useEffect, useCallback } from "react";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Database,
  Search,
  Filter,
  RefreshCw,
  PlaneLanding,
  PlaneTakeoff,
  Eye,
  ChevronLeft,
  ChevronRight,
  CalendarIcon,
  Download,
  FileSpreadsheet,
} from "lucide-react";
import { api, cn } from "../lib/utils";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { toast } from "sonner";

export default function FlightsPage() {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const limit = 20;

  // Filters — default to the last 7 days so the page never scans full history
  const defaultFrom = () => { const d = new Date(); d.setDate(d.getDate() - 7); d.setHours(0, 0, 0, 0); return d; };
  const [search, setSearch] = useState("");
  const [deepSearch, setDeepSearch] = useState(false);
  const [legFilter, setLegFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [msgCodeFilter, setMsgCodeFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(null);
  const [source, setSource] = useState("hot");

  const buildQueryParams = useCallback((forExport = false) => {
    const params = new URLSearchParams();
    
    if (!forExport) {
      params.append("limit", limit.toString());
      params.append("skip", (page * limit).toString());
    } else {
      params.append("limit", "10000"); // Get all records for export
    }

    if (search) params.append("search", search);
    if (search && deepSearch) params.append("deep", "true");
    if (legFilter !== "all") params.append("leg", legFilter);
    if (statusFilter !== "all") params.append("status", statusFilter);
    if (msgCodeFilter !== "all") params.append("msg_code", msgCodeFilter);
    if (dateFrom) params.append("date_from", format(dateFrom, "yyyy-MM-dd'T'00:00:00"));
    if (dateTo) params.append("date_to", format(dateTo, "yyyy-MM-dd'T'23:59:59"));
    
    return params;
  }, [page, search, deepSearch, legFilter, statusFilter, msgCodeFilter, dateFrom, dateTo]);

  const fetchFlights = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      try {
        const params = buildQueryParams(false);
        const countParams = new URLSearchParams(params);
        countParams.delete("limit"); countParams.delete("skip");

        const [flightsRes, countRes] = await Promise.all([
          api.get(`/api/flights?${params}`),
          api.get(`/api/flights/count?${countParams}`),
        ]);

        setFlights(flightsRes.data);
        setTotal(countRes.data.total);
        setSource(countRes.data.source || "hot");
      } catch (error) {
        console.error("Failed to fetch flights:", error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [buildQueryParams]
  );

  useEffect(() => {
    fetchFlights();
  }, [fetchFlights]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchFlights();
  };

  const clearFilters = () => {
    setSearch("");
    setDeepSearch(false);
    setLegFilter("all");
    setStatusFilter("all");
    setMsgCodeFilter("all");
    setDateFrom(defaultFrom());
    setDateTo(null);
    setPage(0);
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const params = buildQueryParams(true);
      const response = await api.get(`/api/flights?${params}`);
      const data = response.data;

      if (data.length === 0) {
        toast.error("No data to export");
        return;
      }

      // Log the export event
      try {
        await api.post(`/api/audit-logs/export`, {
          export_type: "excel",
          record_count: data.length,
          filters: {
            search: search || null,
            leg: legFilter !== "all" ? legFilter : null,
            status: statusFilter !== "all" ? statusFilter : null,
            date_from: dateFrom ? format(dateFrom, "yyyy-MM-dd") : null,
            date_to: dateTo ? format(dateTo, "yyyy-MM-dd") : null,
          }
        });
      } catch (auditError) {
        console.warn("Failed to log export event:", auditError);
      }

      // Prepare data for Excel
      const excelData = data.map((flight) => ({
        "Flight No": flight.flight_no || "",
        "Airline": flight.airline_code || "",
        "Type": flight.leg === "A" ? "Arrival" : flight.leg === "D" ? "Departure" : flight.leg || "",
        "Origin": flight.origin || "",
        "Destination": flight.destination || "",
        "Scheduled Time": flight.scheduled_time || "",
        "Estimated Time": flight.estimated_time || "",
        "Actual Time": flight.actual_time || "",
        "Status": flight.status || "SCHEDULED",
        "Stand": flight.stand_code || "",
        "Gate": flight.gate_code || "",
        "Remark": flight.remark_code || "",
        "Message Code": flight.msg_code || "",
        "Sender IP": flight.sender_ip || "",
        "Received At": flight.received_at ? format(new Date(flight.received_at), "yyyy-MM-dd HH:mm:ss") : "",
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      ws["!cols"] = [
        { wch: 12 }, // Flight No
        { wch: 10 }, // Airline
        { wch: 10 }, // Type
        { wch: 8 },  // Origin
        { wch: 12 }, // Destination
        { wch: 18 }, // Scheduled Time
        { wch: 18 }, // Estimated Time
        { wch: 18 }, // Actual Time
        { wch: 12 }, // Status
        { wch: 8 },  // Stand
        { wch: 8 },  // Gate
        { wch: 15 }, // Remark
        { wch: 12 }, // Message Code
        { wch: 16 }, // Sender IP
        { wch: 20 }, // Received At
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Flight Data");

      // Generate filename with date range
      let filename = "FlightCore_Export";
      if (dateFrom) filename += `_from_${format(dateFrom, "yyyyMMdd")}`;
      if (dateTo) filename += `_to_${format(dateTo, "yyyyMMdd")}`;
      filename += `_${format(new Date(), "yyyyMMdd_HHmmss")}.xlsx`;

      // Download
      XLSX.writeFile(wb, filename);
      toast.success(`Exported ${data.length} records to Excel`);
    } catch (error) {
      console.error("Failed to export:", error);
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "---";
    const date = new Date(isoString);
    return date.toLocaleString("en-MY", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Kuala_Lumpur"
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      SCHEDULED: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
      DELAYED: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
      CANCELLED: "bg-red-500/10 text-red-500 border-red-500/20",
      DEPARTED: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      ARRIVED: "bg-green-500/10 text-green-500 border-green-500/20",
      LANDED: "bg-green-500/10 text-green-500 border-green-500/20",
      BOARDING: "bg-purple-500/10 text-purple-500 border-purple-500/20",
      "FINAL CALL": "bg-orange-500/10 text-orange-500 border-orange-500/20",
      "GATE OPEN": "bg-cyan-500/10 text-cyan-500 border-cyan-500/20",
      "GATE CLOSED": "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
    };

    return (
      <Badge
        variant="outline"
        className={statusColors[status] || statusColors.SCHEDULED}
      >
        {status || "SCHEDULED"}
      </Badge>
    );
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Layout>
      <div className="container mx-auto p-6 md:p-8" data-testid="flights-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              INTEGRATION DATA
            </h1>
            <p className="text-muted-foreground mt-1">
              View and search all received integration messages
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground" data-testid="flights-total">
              {total} records in range
            </span>
            {source === "all" && (
              <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-500 border-amber-500/20" data-testid="archive-included-badge">
                includes archive
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={exportToExcel}
              disabled={exporting || total === 0}
              data-testid="export-excel-btn"
            >
              {exporting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4 mr-2" />
              )}
              Export Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchFlights(true)}
              disabled={refreshing}
              data-testid="refresh-flights-btn"
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-card/50 border-border mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="space-y-4">
              {/* Row 1: Search and basic filters */}
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={deepSearch ? "Deep search (contains, slower)..." : "Search flight, airline, route (starts with)..."}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10 bg-background"
                      data-testid="search-input"
                    />
                  </div>
                  <label className="flex items-center gap-1.5 mt-1.5 text-[11px] text-muted-foreground cursor-pointer select-none">
                    <input type="checkbox" checked={deepSearch} onChange={(e) => setDeepSearch(e.target.checked)} className="h-3 w-3 accent-primary" data-testid="deep-search-toggle" />
                    Deep search (match anywhere in text — slower on large ranges)
                  </label>
                </div>

                <Select value={legFilter} onValueChange={setLegFilter}>
                  <SelectTrigger className="w-[140px] bg-background" data-testid="leg-filter">
                    <SelectValue placeholder="Flight Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="A">Arrivals</SelectItem>
                    <SelectItem value="D">Departures</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px] bg-background" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                    <SelectItem value="DELAYED">Delayed</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled</SelectItem>
                    <SelectItem value="DEPARTED">Departed</SelectItem>
                    <SelectItem value="ARRIVED">Arrived</SelectItem>
                    <SelectItem value="BOARDING">Boarding</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={msgCodeFilter} onValueChange={setMsgCodeFilter}>
                  <SelectTrigger className="w-[160px] bg-background" data-testid="msgcode-filter">
                    <SelectValue placeholder="Msg Code" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Msg Codes</SelectItem>
                    <SelectItem value="FIDASM1">FIDASM1</SelectItem>
                    <SelectItem value="FIDASM2">FIDASM2</SelectItem>
                    <SelectItem value="FIDTMATMS">FIDTMATMS</SelectItem>
                    <SelectItem value="FIDTMAIRL">FIDTMAIRL</SelectItem>
                    <SelectItem value="GASSNDALC">GASSNDALC</SelectItem>
                    <SelectItem value="FIDDLYDET">FIDDLYDET</SelectItem>
                    <SelectItem value="FIDDLTFLT">FIDDLTFLT</SelectItem>
                    <SelectItem value="FIDUNDOFLT">FIDUNDOFLT</SelectItem>
                    <SelectItem value="FIDFCLNG1">FIDFCLNG1</SelectItem>
                    <SelectItem value="FIDFCLNG2">FIDFCLNG2</SelectItem>
                    <SelectItem value="FIDFCRCM2">FIDFCRCM2</SelectItem>
                    <SelectItem value="FIDFCCHK">FIDFCCHK</SelectItem>
                    <SelectItem value="FIDADMRAD">FIDADMRAD</SelectItem>
                    <SelectItem value="FIDAFS">FIDAFS</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Row 2: Date range filters */}
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">From Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[180px] justify-start text-left font-normal bg-background",
                          !dateFrom && "text-muted-foreground"
                        )}
                        data-testid="date-from-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateFrom ? format(dateFrom, "MMM dd, yyyy") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateFrom}
                        onSelect={setDateFrom}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">To Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[180px] justify-start text-left font-normal bg-background",
                          !dateTo && "text-muted-foreground"
                        )}
                        data-testid="date-to-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateTo ? format(dateTo, "MMM dd, yyyy") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateTo}
                        onSelect={setDateTo}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <Button type="submit" data-testid="apply-filters-btn">
                  Apply Filters
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={clearFilters}
                  data-testid="clear-filters-btn"
                >
                  Clear All
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-card/50 border-border">
          <CardContent className="p-0">
            <ScrollArea className="w-full">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground font-medium">
                      Type
                    </TableHead>
                    <TableHead className="text-muted-foreground font-medium">
                      Msg Code
                    </TableHead>
                    <TableHead className="text-muted-foreground font-medium">
                      Flight No
                    </TableHead>
                    <TableHead className="text-muted-foreground font-medium">
                      Route
                    </TableHead>
                    <TableHead className="text-muted-foreground font-medium">
                      Scheduled
                    </TableHead>
                    <TableHead className="text-muted-foreground font-medium">
                      Status
                    </TableHead>
                    <TableHead className="text-muted-foreground font-medium">
                      Sender IP
                    </TableHead>
                    <TableHead className="text-muted-foreground font-medium">
                      Received
                    </TableHead>
                    <TableHead className="text-muted-foreground font-medium text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                        <p className="text-muted-foreground mt-2">
                          Loading flights...
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : flights.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <Database className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-muted-foreground">
                          No flights found
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Try adjusting your filters or search criteria
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    flights.map((flight) => (
                      <TableRow
                        key={flight.id}
                        className="border-border cursor-pointer hover:bg-muted/50"
                        onClick={() => setSelectedFlight(flight)}
                        data-testid={`flight-row-${flight.id}`}
                      >
                        <TableCell>
                          {flight.leg === "A" ? (
                            <div className="flex items-center gap-2 text-green-500">
                              <PlaneLanding className="h-4 w-4" />
                              <span className="text-xs font-medium">ARR</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-blue-500">
                              <PlaneTakeoff className="h-4 w-4" />
                              <span className="text-xs font-medium">DEP</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-muted">
                            {flight.msg_code || "---"}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">
                          {flight.flight_no || "---"}
                        </TableCell>
                        <TableCell>
                          <span className="text-muted-foreground">
                            {flight.origin || "---"} → {flight.destination || "---"}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {flight.scheduled_time || "---"}
                        </TableCell>
                        <TableCell>{getStatusBadge(flight.status)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm font-mono">
                          {flight.sender_ip || "---"}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDateTime(flight.received_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFlight(flight);
                            }}
                            data-testid={`view-flight-${flight.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Showing {page * limit + 1} to{" "}
                  {Math.min((page + 1) * limit, total)} of {total} results
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground px-2">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPage((p) => Math.min(totalPages - 1, p + 1))
                    }
                    disabled={page >= totalPages - 1}
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Flight Detail Dialog */}
        <Dialog
          open={!!selectedFlight}
          onOpenChange={(open) => !open && setSelectedFlight(null)}
        >
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                {selectedFlight?.leg === "A" ? (
                  <PlaneLanding className="h-5 w-5 text-green-500" />
                ) : (
                  <PlaneTakeoff className="h-5 w-5 text-blue-500" />
                )}
                Flight {selectedFlight?.flight_no || "Details"}
              </DialogTitle>
            </DialogHeader>

            {selectedFlight && (
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Flight No</p>
                    <p className="font-medium">
                      {selectedFlight.flight_no || "---"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Airline</p>
                    <p className="font-medium">
                      {selectedFlight.airline_code || "---"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Type</p>
                    <p className="font-medium">
                      {selectedFlight.leg === "A" ? "Arrival" : "Departure"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Origin</p>
                    <p className="font-medium">
                      {selectedFlight.origin || "---"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Destination</p>
                    <p className="font-medium">
                      {selectedFlight.destination || "---"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    {getStatusBadge(selectedFlight.status)}
                  </div>
                </div>

                {/* Times */}
                <div className="border-t border-border pt-4">
                  <h4 className="text-sm font-medium mb-3">Schedule</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Scheduled</p>
                      <p className="font-medium">
                        {selectedFlight.scheduled_time || "---"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Estimated</p>
                      <p className="font-medium">
                        {selectedFlight.estimated_time || "---"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Actual</p>
                      <p className="font-medium">
                        {selectedFlight.actual_time || "---"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="border-t border-border pt-4">
                  <h4 className="text-sm font-medium mb-3">Location</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Stand</p>
                      <p className="font-medium">
                        {selectedFlight.stand_code || "---"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Gate</p>
                      <p className="font-medium">
                        {selectedFlight.gate_code || "---"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Remark</p>
                      <p className="font-medium">
                        {selectedFlight.remark_code || "---"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Metadata */}
                <div className="border-t border-border pt-4">
                  <h4 className="text-sm font-medium mb-3">Message Info</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Message Code
                      </p>
                      <p className="font-medium">{selectedFlight.msg_code}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Sender IP
                      </p>
                      <p className="font-medium font-mono text-sm">
                        {selectedFlight.sender_ip || "---"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Received At
                      </p>
                      <p className="font-medium">
                        {formatDateTime(selectedFlight.received_at)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Raw Payload */}
                <div className="border-t border-border pt-4">
                  <h4 className="text-sm font-medium mb-3">Raw Payload</h4>
                  <ScrollArea className="h-[200px]">
                    <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
                      {JSON.stringify(selectedFlight.raw_payload, null, 2)}
                    </pre>
                  </ScrollArea>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
